import { todayIsoInZone } from '@harvus/shared';
// ============================================================
// cashFlowFormat.js — the Cash Flow Projection Report's pure display rules.
//
// The report's ARITHMETIC is entirely server-side (api/src/cashFlowProjection.js)
// and this file deliberately adds none of it. What lives here are the five
// decisions the page makes about PRESENTING that arithmetic, each of which has
// edge cases worth pinning without a report on screen:
//
//  1. A ZERO IS A DASH, NOT "$0". A projection matrix is mostly empty — a deal
//     with two flows across forty quarters fills two cells — and a grid of "$0"
//     reads as forty measurements rather than two. More importantly it makes a
//     period nobody projected look indistinguishable from one projected to net
//     out exactly, which is the same "unknown is not zero" rule the cost filter
//     and the NAICS report already apply to a missing figure.
//
//  2. A NEGATIVE IS PARENTHESIZED. Accounting convention, and on a cash-flow
//     report the sign IS the content: a leading minus in a right-aligned column
//     of similar-width numbers is genuinely easy to miss, and mistaking a
//     capital call for a distribution inverts the meaning of the row. Colour
//     carries the same signal, but colour alone fails for a colour-blind reader
//     and in print.
//
//  3. A RATE IS A PERCENTAGE OR A DASH, AND A DASH IS NEVER A ZERO. An IRR
//     that cannot be solved — capital out with nothing modelled coming back, a
//     capital item with no Sage tagging, a book of nothing but forecasts — is
//     not 0%. Rendering it as one would report a position as breaking even when
//     the honest answer is that the question is unanswerable from what has been
//     recorded, and the reason (which the API sends in words) is what tells the
//     reader which of those it is.
//
//  4. SEARCH IS TOKEN-AND ACROSS THE WHOLE ROW. "riverbend land" has to match a
//     deal named Riverbend holding a capital item called Land — no single field
//     holds both, so a per-field substring test could never match it. Every
//     term having to appear means adding a word NARROWS the result, which is
//     what a reader expects of a search box; matching any term would widen it.
//
//  5. THE CHART IS ONE AXIS, WHICH IS WHAT MAKES THE COMPARISON LEGIBLE. Our
//     rates and the market's are the same kind of number but they are not the
//     same measurement: a rate is a point and the market is a series. So all
//     three are drawn as CUMULATIVE GROWTH indexed to zero at the start of the
//     window — the market's from its measured levels, ours by compounding the
//     rate the way an IRR asserts it compounds — and every series then shares
//     one y scale. Two scales here would be the dual-axis mistake wearing a
//     comparison's clothes.
// ============================================================

// Compact money for a dense matrix cell. Full precision belongs in the export
// (api/src/cashFlowExport.js, where amounts are numbers rather than strings) —
// the reader of a hundred-column grid is comparing magnitudes, and
// "$1,234,567" at that width is a wall.
//
// Returns null for a zero or missing value, which the caller renders as a dash
// (see rule 1). A non-numeric value is null too, rather than "$NaN".
export function compactMoney(v) {
  if (v == null || v === '' || isNaN(Number(v))) return null;
  const n = Number(v);
  if (n === 0) return null;
  const abs = Math.abs(n);
  let body;
  // One decimal place up to 10 of a unit, none above it — "$9.4M" is worth the
  // decimal, "$94.3M" is not, and dropping it keeps the column narrow.
  if (abs >= 1e9) body = `$${(abs / 1e9).toFixed(abs >= 1e10 ? 0 : 1)}B`;
  else if (abs >= 1e6) body = `$${(abs / 1e6).toFixed(abs >= 1e7 ? 0 : 1)}M`;
  else if (abs >= 1e3) body = `$${Math.round(abs / 1e3)}k`;
  else body = `$${Math.round(abs)}`;
  return n < 0 ? `(${body})` : body;
}

/** Token-AND search over everything visible on a deal row (see rule 4). */
export function dealMatchesQuery(deal, query) {
  const needle = (query || '').trim().toLowerCase();
  if (!needle) return true;
  if (!deal) return false;
  const hay = [
    deal.name, deal.status,
    ...(deal.sources || []),
    ...(deal.details || []).flatMap((x) => [x && x.label, x && x.detailType]),
  ].filter(Boolean).join(' ').toLowerCase();
  return needle.split(/\s+/).every((t) => hay.includes(t));
}

// Which column the reader should be looking at. The axis is the span of the
// projected money, so today is often nowhere near either end — and when it IS
// outside the axis the honest answer is the nearest edge, not "no column":
// a projection that finished last year should open on its last period rather
// than refusing to scroll and looking broken.
export function currentPeriodIndex(periods, todayISO) {
  const list = Array.isArray(periods) ? periods : [];
  if (!list.length) return -1;
  const today = todayISO || todayIsoInZone();
  for (let i = 0; i < list.length; i += 1) {
    const p = list[i] || {};
    if (p.start && p.end && p.start <= today && today <= p.end) return i;
  }
  if (list[0].start && today < list[0].start) return 0;
  return list.length - 1;
}

/**
 * Is this row entirely empty — every cell the reader can see a dash or "$0"?
 *
 * WHAT COUNTS AS ZERO IS WHAT THE ROW SHOWS, NOT WHAT IT IS MADE OF. A capital
 * item that took $1 in and paid $1 out in the same period nets to nothing, and
 * on the Net view that row is a line of dashes — so it is an all-zero row and
 * hiding it is what the reader asked for. Judging on the underlying events
 * instead would leave it on screen contributing nothing, which is exactly the
 * noise the filter exists to remove.
 *
 * It follows the CHOSEN MEASURE for the period cells, because that is what
 * those cells render: the same row is empty under Net and non-empty under
 * Cash in, and hiding a row that is plainly showing numbers would be a bug.
 * The before / after / total cells are always net (as the table renders them)
 * and are always judged, whether or not those columns happen to be on screen —
 * a row holding money outside the window is not an empty row, and reading the
 * column-visibility flags here would be circular, since they are themselves
 * derived from the rows that survive this filter.
 *
 * ANYTHING THAT ROUNDS TO ZERO DOLLARS IS ZERO. `compactMoney` renders a
 * sub-dollar residue as "$0" rather than a dash, so floating-point dust left by
 * summing a series would otherwise keep a visually empty row on screen.
 *
 * Hidden rows contribute nothing to the dollar, so the portfolio total row and
 * the KPI strip above it — which come from the server and are never filtered —
 * still tie to what is left on screen.
 */
export function rowIsAllZero(row, measure) {
  if (!row) return true;
  const cells = [
    row.before && row.before.net,
    ...(((row.series && row.series[measure || 'net']) || [])),
    row.after && row.after.net,
    row.totals && row.totals.net,
  ];
  return cells.every((v) => {
    if (v == null || v === '' || isNaN(Number(v))) return true;
    return Math.round(Number(v)) === 0;
  });
}

// ---- rates -----------------------------------------------------------------

/**
 * A solved rate as a percentage, or null (see rule 3).
 *
 * NULL IS NOT ZERO AND MUST NOT RENDER AS ONE. Every null the API sends carries
 * a `reason`, and the caller shows a dash with that reason rather than "0.0%" —
 * a position with capital out and nothing modelled coming back has not broken
 * even, it has not been answered.
 *
 * One decimal place: an IRR is solved to four and displayed to one, because the
 * third significant figure of a rate resting on a broker's opinion of value is
 * false precision. A rate past ±1000% (a series with a rounding-dust outflow
 * against a real inflow can solve to thousands) is capped so a column of
 * numbers stays readable, and the cap is SHOWN as a cap rather than silently
 * truncating the figure.
 */
export function formatRate(v) {
  if (v == null || v === '' || isNaN(Number(v))) return null;
  const n = Number(v) * 100;
  if (!Number.isFinite(n)) return null;
  if (n > 1000) return '>1000%';
  if (n < -1000) return '<-1000%';
  return `${n.toFixed(1)}%`;
}

/**
 * A rate block's tone: a return is green, a loss red, an unsolved rate neither.
 *
 * Deliberately not keyed on a threshold anybody chose — "good" depends on the
 * cost of capital, which this report does not know. Zero is the only line it
 * draws, and it is a fact rather than a judgement.
 */
export function rateSign(v) {
  if (v == null || v === '' || isNaN(Number(v))) return 'none';
  const n = Number(v);
  return n > 0 ? 'up' : n < 0 ? 'down' : 'flat';
}

/**
 * A cumulative growth figure for a chart label: "+69.9%", "-12.4%", "0%".
 *
 * DELIBERATELY NOT `formatRate`, which caps at ±1000% so a column of rates
 * stays readable. Cumulative growth legitimately runs into the thousands — a
 * 26% IRR compounds to +900% over ten years — and a curve whose end label read
 * ">1000%" would be a chart that refuses to say what it drew. So no cap, a
 * thousands separator past 100%, and the sign always shown, because the whole
 * question the chart answers is which way and how far.
 */
export function formatGrowth(v, opts) {
  if (v == null || v === '' || isNaN(Number(v))) return null;
  const n = Number(v) * 100;
  if (!Number.isFinite(n)) return null;
  // AN AXIS PASSES ITS OWN PRECISION, because a tick ladder has to be uniform:
  // "+100%" beside "+80.0%" reads as two different measurements of different
  // accuracy when they are the same gridline scale. Left to itself the default
  // drops the decimal past 100% (where it is noise on a curve label) and keeps
  // it below (where it is the difference between 8% and 8.4%).
  const dp = opts && opts.decimals != null ? Math.max(0, Math.min(2, Number(opts.decimals))) : null;
  if (dp === null && Math.round(n * 10) === 0) return '0%';
  if (dp !== null && Math.round(n * (10 ** dp)) === 0) return '0%';
  const abs = Math.abs(n);
  const places = dp !== null ? dp : (abs >= 100 ? 0 : 1);
  const body = abs.toLocaleString('en-US', { minimumFractionDigits: places, maximumFractionDigits: places });
  return `${n < 0 ? '-' : '+'}${body}%`;
}

// A 1 / 2 / 2.5 / 5 ladder, scaled by powers of ten. Gridline labels a reader
// can hold in their head — 20% / 40% / 60% rather than 37.4% / 74.8%.
function niceStep(span, intervals) {
  const raw = Math.abs(span) / Math.max(1, intervals);
  if (!(raw > 0)) return 0.1;
  const mag = 10 ** Math.floor(Math.log10(raw));
  for (const m of [1, 2, 2.5, 5]) if (raw <= m * mag) return m * mag;
  return 10 * mag;
}

/**
 * The chart's shared y scale, its gridlines, and the width of its x axis.
 *
 * ZERO IS ALWAYS INSIDE IT. Every series is indexed to zero at the start of the
 * window, so the zero line is where all three begin and the only reference a
 * reader measures against; an extent fitted to the data alone could exclude it
 * and the curves would all appear to start from nowhere.
 *
 * ROUNDED OUT TO A WHOLE STEP on both ends so the topmost and bottommost
 * gridlines are labelled values rather than the ragged edge of the data, and
 * floored (`minSpan`) so a book of flat single-digit returns does not render as
 * a chart of dramatic swings.
 *
 * `tMax` is in YEARS ELAPSED, not a date. The market window is trailing and our
 * rates are forward projections — plotting ours on the market's calendar dates
 * would claim our book had already done that, in the past. So the axis is the
 * span both sides share, and the caption carries the market's real dates.
 */
export function growthAxis(series, keys, opts) {
  const rows = Array.isArray(series) ? series : [];
  const fields = Array.isArray(keys) && keys.length ? keys : ['market', 'unrealized', 'projected'];
  const minSpan = Number.isFinite(Number(opts && opts.minSpan)) ? Math.abs(Number(opts.minSpan)) : 0.2;

  let lo = 0;
  let hi = 0;
  let tMax = 0;
  for (const r of rows) {
    const t = Number(r && r.t);
    if (Number.isFinite(t)) tMax = Math.max(tMax, t);
    for (const k of fields) {
      const v = r ? r[k] : null;
      if (v == null || isNaN(Number(v))) continue;
      lo = Math.min(lo, Number(v));
      hi = Math.max(hi, Number(v));
    }
  }
  if (hi - lo < minSpan) hi = lo + minSpan;

  const step = niceStep(hi - lo, 5);
  const min = Math.floor(lo / step) * step;
  const max = Math.ceil(hi / step) * step;

  const ticks = [];
  // Walk in integer multiples rather than accumulating `+= step`, or a decimal
  // step drifts and the labels come out as 0.30000000000000004.
  for (let i = Math.round(min / step); i <= Math.round(max / step) + 1e-9; i += 1) {
    const v = i * step;
    if (v > max + 1e-9) break;
    ticks.push(Number(v.toFixed(6)));
  }
  return { min, max, step, ticks, tMax: Number(tMax.toFixed(4)) };
}

/**
 * One series as contiguous runs of plottable points.
 *
 * A GAP BREAKS THE LINE RATHER THAN BEING BRIDGED. A rate that does not solve
 * contributes no curve at all (its whole series is null, so this returns
 * nothing and the caller draws nothing) — but joining across a hole would draw
 * a straight segment through periods the data says nothing about, which is a
 * claim the chart is not entitled to make.
 */
export function growthSegments(series, key) {
  const out = [];
  let run = [];
  for (const r of (Array.isArray(series) ? series : [])) {
    const t = Number(r && r.t);
    const v = r ? r[key] : null;
    if (!Number.isFinite(t) || v == null || isNaN(Number(v))) {
      if (run.length) out.push(run);
      run = [];
      continue;
    }
    run.push({ t, v: Number(v) });
  }
  if (run.length) out.push(run);
  return out;
}

/**
 * The last plottable point of a series — what the direct end label prints.
 *
 * Returns null where the series has none, so the caller omits the label rather
 * than printing a zero for a curve it never drew.
 */
export function growthEnd(series, key) {
  const rows = Array.isArray(series) ? series : [];
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const r = rows[i];
    const v = r ? r[key] : null;
    if (v != null && !isNaN(Number(v)) && Number.isFinite(Number(r && r.t))) {
      return { t: Number(r.t), v: Number(v), date: (r && r.date) || null };
    }
  }
  return null;
}

/**
 * Stack a set of end labels so they do not overlap.
 *
 * Three curves converging at the right edge print three labels on top of each
 * other, which is worse than no labels. Positions are nudged apart from the
 * top down by the label's own height, preserving their ORDER — a label that
 * jumped past its neighbour would point at the wrong line.
 */
export function stackLabels(items, minGap) {
  const gap = Number.isFinite(Number(minGap)) && Number(minGap) > 0 ? Number(minGap) : 14;
  // ABSENCE IS CHECKED BEFORE THE COERCION, because `Number(null)` is 0 — a
  // finite number, and a legitimate y (the top of the plot) — so a label with
  // no position would be stacked at the top of the chart pointing at nothing.
  const sorted = (Array.isArray(items) ? items : [])
    .filter((it) => it && it.y != null && it.y !== '' && Number.isFinite(Number(it.y)))
    .map((it) => ({ ...it, y: Number(it.y) }))
    .sort((a, b) => a.y - b.y);
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i].y - sorted[i - 1].y < gap) sorted[i].y = sorted[i - 1].y + gap;
  }
  return sorted;
}

/**
 * ONE DATED SERIES SPLIT AT THE MEASURED/ASSUMED SEAM.
 *
 * The deployed-dollar path is measured up to the index's last observation and
 * carried forward at an assumed rate past it, so the two halves are different
 * kinds of claim and must not be drawn the same. Returns runs for each, so the
 * caller strokes one solid and one dashed.
 *
 * THE DASHED RUN STARTS ON THE LAST MEASURED POINT, deliberately. Split
 * strictly by the flag, the two halves would be adjacent but disjoint and the
 * line would show a one-pixel gap exactly where the reader is looking for the
 * seam — which reads as missing data rather than as a change of basis. Sharing
 * the boundary point joins them without either half claiming a value the other
 * measured.
 *
 * A GAP STILL BREAKS THE LINE within either half (`growthSegments`'s rule): a
 * date the index cannot price is an absence, not a straight line through it.
 */
export function splitAtAssumed(series, key) {
  const rows = Array.isArray(series) ? series : [];
  const solid = [];
  const dashed = [];
  let run = null;
  let mode = null;
  let lastSolid = null;

  for (const r of rows) {
    const v = r ? r[key] : null;
    if (v == null || isNaN(Number(v))) { run = null; mode = null; continue; }
    const pt = { t: Number(r.t), v: Number(v), date: r.date || null };
    const assumed = !!(r && r.assumed);
    if (mode !== assumed) {
      run = [];
      if (assumed && lastSolid) run.push(lastSolid);
      (assumed ? dashed : solid).push(run);
      mode = assumed;
    }
    run.push(pt);
    if (!assumed) lastSolid = pt;
  }
  return { solid: solid.filter((r) => r.length > 1), dashed: dashed.filter((r) => r.length > 1) };
}

/**
 * CALENDAR TICKS for the dated axis — one per whole year, thinned so the labels
 * never collide, and always including the first and last point.
 *
 * Derived from the series rather than from a chosen window: both ends of this
 * axis are facts about when the money moved, which is the correction the
 * trailing-window chart needed.
 */
export function yearTicks(series, maxTicks) {
  const rows = (Array.isArray(series) ? series : []).filter((r) => r && r.date && Number.isFinite(Number(r.t)));
  if (!rows.length) return [];
  const cap = Number.isFinite(Number(maxTicks)) && Number(maxTicks) > 1 ? Math.floor(Number(maxTicks)) : 10;

  const first = rows[0];
  const last = rows[rows.length - 1];
  const y0 = Number(first.date.slice(0, 4));
  const y1 = Number(last.date.slice(0, 4));
  const every = Math.max(1, Math.ceil((y1 - y0 + 1) / cap));

  const out = [];
  for (let y = y0 + 1; y <= y1; y += every) {
    // The first sampled point on or after 1 Jan, so a tick always sits on a
    // real point of the path rather than on an interpolated x nobody plotted.
    const hit = rows.find((r) => r.date >= `${y}-01-01`);
    if (hit && !out.some((o) => o.label === String(y))) out.push({ t: Number(hit.t), label: String(y) });
  }
  // Both ends always, since "when did the money start" is the axis's whole
  // claim and a thinned tick list can drop it.
  if (!out.length || out[0].t > Number(first.t) + 1e-9) out.unshift({ t: Number(first.t), label: first.date.slice(0, 4) });
  return out;
}

// ---- row/cell notes ---------------------------------------------------------

/**
 * Why a deal isn't projected, why a capital item's exit is a Sage entity, the
 * caveat behind a deal's unrealized rate — every one of these used to be its
 * own italic fragment or an inline warning glyph, each carrying its own native
 * `title` tooltip. That's a different interaction for every note, none of them
 * work on a touchscreen, and a reader printing the report loses all of them.
 * The table now carries a single `*` at the row the note is about; this
 * collects the sentences themselves into ONE list for the disclosure at the
 * foot of the report (see ReportNotesDisclosure).
 *
 * GROUPED BY THE NOTE'S OWN TEXT, not printed once per row. Most of these
 * sentences come from a small fixed set (`cashFlowProjection.js`'s handful of
 * "why this isn't projected" reasons), so a book-wide run shares the same
 * sentence across many deals — one footnote naming every affected row reads
 * far better than the same paragraph repeated forty times, which is the same
 * instinct behind every other grouped count already on this page
 * (`unavailableSources`, `actualsOnly`, …).
 *
 * MIRRORS WHAT THE TABLE ACTUALLY SHOWS, or a footnote for a row that isn't on
 * screen is worse than none: `hideZeroRows` drops the same capital items here
 * that it drops there, and an item's own note is only collected once its deal
 * has more than one surviving item — a single-item deal's row IS that item's
 * row (the table never renders it separately), so the item's note already
 * reached the list as the deal's own.
 */
export function collectRowNotes(rows, measure, hideZeroRows) {
  const list = Array.isArray(rows) ? rows : [];
  const groups = new Map();
  const add = (tone, text, label) => {
    if (!text || !label) return;
    const gKey = `${tone}::${text}`;
    let g = groups.get(gKey);
    if (!g) { g = { tone, text, labels: [] }; groups.set(gKey, g); }
    if (!g.labels.includes(label)) g.labels.push(label);
  };

  for (const deal of list) {
    if (!deal) continue;
    if (!deal.projected) {
      add('info', deal.reason || (deal.hasActuals
        ? 'Actuals only — nothing projected forward.'
        : 'Not projected.'), deal.name);
    }
    // A DEAL RATE THAT CREDITS NONE OF WHAT AN UNVALUED SIBLING HOLDS IS REAL
    // AND INCOMPLETE (see rollupIrr) — this fires whether or not the deal
    // itself is projected, exactly as the ⚠ it replaces did.
    if (deal.irr && deal.irr.unrealized && deal.irr.unrealized.caveat) {
      add('warn', deal.irr.unrealized.caveat, deal.name);
    }
    const details = Array.isArray(deal.details) ? deal.details : [];
    const shown = hideZeroRows ? details.filter((item) => !rowIsAllZero(item, measure)) : details;
    if (shown.length > 1) {
      for (const item of shown) {
        if (!item) continue;
        const label = `${deal.name} — ${item.label}`;
        if (item.entity) {
          const kind = item.entity.kind === 'entity' ? 'entity' : 'location';
          const how = item.entity.source === 'user' ? 'by hand' : 'from the Sage hierarchy';
          add('info', `Sage ${kind} code ${item.entity.code || '—'}, attached to the deal ${how}. Its postings and borrowing are counted on this deal; nothing projects it forward.`, label);
        } else if (item.reason) {
          add('info', item.reason, label);
        }
      }
    }
  }

  const MAX_LABELS = 6;
  return Array.from(groups.values()).map((g, i) => ({
    key: `row-${i}`,
    tone: g.tone,
    body: `${g.labels.length > MAX_LABELS
      ? `${g.labels.slice(0, MAX_LABELS).join(', ')}, and ${g.labels.length - MAX_LABELS} more`
      : g.labels.join(', ')}: ${g.text}`,
  }));
}
